import { reportPlanSummaryMap } from "@ishvaram/domain-data/report-plan-summaries";

import type { CollectInputsPlacementProps } from "@/components/OfferPlacement";
import { CollectInputsCard } from "@/components/OfferPlacement.collect-inputs-card";
import { RUNTIME_OFFER_PLACEMENT_NUMEROLOGY_LOOKUP_BELOW_RESULT } from "@/lib/offer-placement-runtime-keys.generated";

/**
 * The numerology pages' report offer: the Numerology Life Report
 * (`numerology_report`), the `numerology` intent's primary offer (rule 40).
 * A numerology page has a name and DOB but not a full birth chart, so it
 * collects the rest on the kundali form instead of opening checkout. The
 * already-entered name and DOB ride in the form link's hash.
 *
 * Copy is pain-led per rule 22 in rule-21 vocabulary (kundali, graha, mulank, bhagyank).
 */
const numerology = reportPlanSummaryMap.numerology_report;

/**
 * Bhagyank (destiny number) → its ruling graha + the life pattern that graha
 * governs. The number is engine-computed (rules 24/29 — rendered from
 * `result.bhagyank`, never a literal); the graha mapping is Ank-Jyotish
 * convention and the pain line is curated prose (rule 22), so it lives here.
 */
const BHAGYANK_GRAHA: Record<number, { en: string; hi: string }> = {
  1: {
    en: "Surya — strong will, but recognition can come late",
    hi: "Surya — mazboot iraada, par pehchaan der se",
  },
  2: {
    en: "Chandra — a sensitive mind that rarely switches off",
    hi: "Chandra — sanvedansheel mann jo shaant nahi hota",
  },
  3: {
    en: "Guru — wisdom and growth that ask for the right timing",
    hi: "Guru — gyaan aur tarakki, par sahi samay maangti hai",
  },
  4: {
    en: "Rahu — sudden ups and downs that are hard to name",
    hi: "Rahu — achanak utaar-chadhaav jinhe samajhna mushkil",
  },
  5: {
    en: "Budh — a quick mind that can scatter its focus",
    hi: "Budh — tez dimaag jiska dhyaan bikhar jaata hai",
  },
  6: {
    en: "Shukra — love and comfort that keep asking for balance",
    hi: "Shukra — prem aur aaram jo santulan maangte hain",
  },
  7: {
    en: "Ketu — a pull away from what should matter most",
    hi: "Ketu — jo cheezein maayne rakhti hain unse doori",
  },
  8: {
    en: "Shani — hard work that keeps meeting delay and tests",
    hi: "Shani — mehnat jo baar-baar der aur imtihaan se guzarti hai",
  },
  9: {
    en: "Mangal — drive and heat that need a channel",
    hi: "Mangal — energy aur garmi jise sahi disha chahiye",
  },
};

function numerologyRelevanceSubhead(bhagyank: number, language: "en" | "hi"): string | null {
  const graha = BHAGYANK_GRAHA[bhagyank];
  if (!graha) return null;
  return language === "hi"
    ? `Aapka Bhagyank ${bhagyank} ${graha.hi}. Yeh wahi graha hai jo aapke number ko chalata hai — apni poori Numerology Life Report se number, uska graha aur upay ek saath samjhein.`
    : `Your Bhagyank ${bhagyank} carries ${graha.en}. That graha rules the number — your full Numerology Life Report reads the number, its graha, name-number harmony, and the upay.`;
}

export function numerologyReportOffer({
  language = "en",
  sourcePath,
  bhagyank,
  name,
  birthDate,
}: {
  language?: "en" | "hi";
  sourcePath: string;
  /** Engine-computed destiny number — personalizes the subhead to its graha. */
  bhagyank?: number;
  name?: string;
  /** ISO yyyy-mm-dd, as the numerology form collects it. */
  birthDate?: string;
}): CollectInputsPlacementProps {
  const params: Record<string, string> = {
    intent: "chart_report",
    source: sourcePath,
    next_report: numerology.id,
  };
  if (birthDate) params.dob = birthDate;
  if (name) params.name = name;
  const headline =
    language === "hi"
      ? "Aapke number theek hain, par unke peeche kaunsa graha hai?"
      : "Your numbers point at the pattern — but which graha is driving it?";
  const subhead =
    (bhagyank !== undefined ? numerologyRelevanceSubhead(bhagyank, language) : null) ??
    genericSubhead(language);
  return {
    mode: "collect_inputs",
    presentation: CollectInputsCard,
    gate: true,
    placement: RUNTIME_OFFER_PLACEMENT_NUMEROLOGY_LOOKUP_BELOW_RESULT,
    planId: numerology.id,
    intent: "chart_report",
    sourcePath,
    language,
    href: `/free-kundali/#${new URLSearchParams(params).toString()}`,
    headline,
    subhead,
    // The headline is a question, so the trust block cannot derive the noun from it.
    reportNoun: language === "hi" ? numerology.name_hi : numerology.name.replace(/ Report$/, ""),
    ctaLabel: language === "hi" ? "जन्म विवरण भरें" : "Enter birth details",
    note: collectInputsNote(language, Boolean(birthDate)),
    customerProblem: "numerology",
    urgency: "medium",
    confidence: 0.8,
    routeRankScore: 90,
    routeRankReason: `numerology_lookup_${numerology.id}`,
    rankedOfferOrder: [numerology.id],
  };
}

function genericSubhead(language: "en" | "hi"): string {
  return language === "hi"
    ? "Mulank aur bhagyank wahi graha dikhate hain jo aapke number ko chalate hain. Apni poori Numerology Life Report se number, graha aur upay ek saath samjhein."
    : "Mulank and bhagyank are ruled by grahas. Get your full Numerology Life Report to read the number, its graha, name-number harmony, and the upay together.";
}

function collectInputsNote(language: "en" | "hi", carriesDob: boolean): string {
  if (carriesDob) {
    return language === "hi"
      ? "आपकी जन्म तिथि आगे साथ जाएगी। बस जन्म समय और स्थान जोड़ें, फिर कुंडली के नीचे रिपोर्ट का भुगतान करें।"
      : "Your date of birth carries over. Add your birth time and place, then pay for the report below your chart.";
  }
  return language === "hi"
    ? "पहले जन्म विवरण भरें। कुंडली बनने के बाद उसके नीचे रिपोर्ट का भुगतान कर सकते हैं।"
    : "Birth details come first. Your chart comes next, with the report's payment step below it.";
}
